/**
 * Envuelve una carga asíncrona para que las llamadas CONCURRENTES con la misma
 * clave compartan una sola petición.
 *
 * Nace del bugfix de la ráfaga de `GET /drivers`: `useDrivers()` lo montan 18
 * sitios, y en "Mi Desempeño" cada `AlertCard` es una instancia más, así que la
 * pantalla del Operador disparaba 35+ peticiones idénticas a la vez.
 *
 * No es una caché: la entrada vive **solo mientras la petición está en vuelo**.
 * Cuando termina (bien o mal) se borra, y la siguiente llamada vuelve a pedir —
 * un componente que se monta después sigue viendo dato fresco, y un error no se
 * queda pegado. La clave separa a quien no debe compartir resultado (otro rol u
 * otro usuario: el Operador y el Supervisor no reciben lo mismo).
 */
export function shareInFlight<A extends unknown[], T>(
  keyOf: (...args: A) => string,
  load: (...args: A) => Promise<T>,
): (...args: A) => Promise<T> {
  const inFlight = new Map<string, Promise<T>>();

  return (...args: A) => {
    const key = keyOf(...args);
    const running = inFlight.get(key);
    if (running) return running;

    const promise = load(...args).finally(() => {
      // Solo borra la suya: si algo la reemplazó, no se lleva por delante a la nueva.
      if (inFlight.get(key) === promise) inFlight.delete(key);
    });
    inFlight.set(key, promise);
    return promise;
  };
}
